import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatNumber, formatPercent, formatRoas } from "@/lib/formatters";
import { Sparklines, SparklinesLine } from "react-sparklines";
import type { CampaignRow } from "./types";

type SortKey = "name" | "objective" | "spend" | "impressions" | "ctr" | "cpc" | "purchases" | "purchaseValue" | "roas";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function TopCampaignsTable({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("spend");
  const [sortAsc, setSortAsc] = useState(false);
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery<CampaignRow[]>({
    queryKey: ["dashboard-campaigns", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("campaigns", params));
      if (!res.ok) throw new Error("Failed to fetch campaigns");
      return res.json();
    },
  });

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const sorted = [...(data || [])].sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortKey === "name") { av = a.name; bv = b.name; }
    else if (sortKey === "objective") { av = a.objective; bv = b.objective; }
    else {
      const aMetrics = a.metrics as Record<string, number> | undefined;
      const bMetrics = b.metrics as Record<string, number> | undefined;
      av = aMetrics?.[sortKey] ?? 0;
      bv = bMetrics?.[sortKey] ?? 0;
    }
    if (typeof av === "string" && typeof bv === "string") return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
  });

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <th
      className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3 cursor-pointer hover:text-gray-700 select-none whitespace-nowrap"
      onClick={() => toggleSort(field)}
      data-testid={`sort-${field}`}
    >
      {label} {sortKey === field ? (sortAsc ? "↑" : "↓") : ""}
    </th>
  );

  const empty = !isLoading && !error && sorted.length === 0;

  return (
    <DashboardWidget title="Campaign Performance" widgetType="table" isEditMode={isEditMode} isLoading={isLoading} error={error instanceof Error ? error.message : null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-campaigns">No campaign data available for this period</div>
      ) : (
        <div className="overflow-auto h-full" data-testid="table-campaigns">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white border-b border-gray-100">
              <tr>
                <SortHeader label="Campaign" field="name" />
                <SortHeader label="Objective" field="objective" />
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Status</th>
                <SortHeader label="Spend" field="spend" />
                <SortHeader label="Impr." field="impressions" />
                <SortHeader label="CTR" field="ctr" />
                <SortHeader label="CPC" field="cpc" />
                <SortHeader label="Purch." field="purchases" />
                <SortHeader label="Revenue" field="purchaseValue" />
                <SortHeader label="ROAS" field="roas" />
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">7d Trend</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const last7 = (c.dailySpend || []).slice(-7);
                const hasMetrics = !!c.metrics;
                return (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 px-3 font-medium text-gray-900 max-w-[200px] truncate">{c.name}</td>
                    <td className="py-2.5 px-3 text-gray-600 text-[10px] uppercase">{c.objective}</td>
                    <td className="py-2.5 px-3">
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-gray-700">{hasMetrics ? formatCurrency(c.metrics.spend) : formatCurrency(parseFloat((c as Record<string, unknown>).dailyBudget as string || "0"))}</td>
                    <td className="py-2.5 px-3 text-gray-700">{hasMetrics ? formatNumber(c.metrics.impressions) : "—"}</td>
                    <td className="py-2.5 px-3 text-gray-700">{hasMetrics ? formatPercent(c.metrics.ctr) : "—"}</td>
                    <td className="py-2.5 px-3 text-gray-700">{hasMetrics ? formatCurrency(c.metrics.cpc) : "—"}</td>
                    <td className="py-2.5 px-3 text-gray-700">{hasMetrics ? formatNumber(c.metrics.purchases) : "—"}</td>
                    <td className="py-2.5 px-3 text-gray-700">{hasMetrics ? formatCurrency(c.metrics.purchaseValue) : "—"}</td>
                    <td className="py-2.5 px-3 font-medium text-gray-900">{hasMetrics ? formatRoas(c.metrics.roas) : "—"}</td>
                    <td className="py-2.5 px-3">
                      {last7.length > 0 && (
                        <div className="w-16 h-5">
                          <Sparklines data={last7} width={64} height={20} margin={2}>
                            <SparklinesLine color="#6366F1" style={{ strokeWidth: 1.5, fill: "none" }} />
                          </Sparklines>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashboardWidget>
  );
}
